import {
  isNumberRequestValidated,
  type NumberRequest,
} from "@/features/number-requests/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import type { ColumnDef } from "@tanstack/react-table";
import { Action } from "./columns-action";
import { NumberRequestStateBadge } from "./state-badge";

/**
 * A hook rather than a constant because every header and the "kind" cell need
 * translating, and `useTranslation` can only be called from a component.
 */
export function useNumberRequestColumns(): ColumnDef<NumberRequest>[] {
  const { t } = useTranslation("numberRequests");
  const { format } = useDateFormat();

  return [
    {
      accessorKey: "kind",
      header: t("numberRequests.columns.kind"),
      cell: ({ row }) => (
        <span className="font-medium">
          {t(`numberRequests.kinds.${row.original.kind}`)}
        </span>
      ),
    },
    {
      accessorKey: "phone_number",
      header: t("numberRequests.columns.number"),
      cell: ({ row }) =>
        row.original.phone_number ? (
          <span className="tabular-nums">{row.original.phone_number}</span>
        ) : (
          <span className="text-muted-foreground text-sm">
            {t("numberRequests.notAssignedYet")}
          </span>
        ),
    },
    {
      accessorKey: "state",
      header: t("numberRequests.columns.state"),
      cell: ({ row }) => <NumberRequestStateBadge state={row.original.state} />,
    },
    {
      accessorKey: "documents",
      header: t("numberRequests.columns.documents"),
      cell: ({ row }) => {
        // Once the line is live the paperwork is behind us: counting it would
        // keep asking a question that has been answered for good.
        if (isNumberRequestValidated(row.original)) return null;
        const documents = row.original.documents?.items || [];
        if (documents.length === 0) return null;
        const provided = documents.filter((document) => document.provided);
        return (
          <span className="text-sm text-muted-foreground tabular-nums">
            {provided.length}/{documents.length}
          </span>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: t("numberRequests.columns.updated"),
      cell: ({ row }) => {
        const history = row.original.history?.items || [];
        const last = history[history.length - 1];
        if (!last) return null;
        return (
          <span className="text-sm text-muted-foreground">
            {format(last.at, "PP")}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right" />,
      cell: ({ row }) => <Action request={row.original} />,
    },
  ];
}
